/**
 * ImportPacientesPage (Phase 23.10)
 * ---------------------------------
 * Importación masiva de pacientes antiguos desde Excel.
 *
 * Flujo:
 *  1. Usuario descarga la plantilla.
 *  2. Rellena los datos en Excel.
 *  3. Sube el fichero. Le mostramos un PREVIEW:
 *      - X pacientes existentes que se actualizarían (solo campos vacíos).
 *      - Y pacientes nuevos que se crearían.
 *      - Z filas con error.
 *  4. Pulsa "Aplicar" → llama a /commit y muestra resultado.
 */
import React, { useState } from 'react';
import { api } from '../lib/api';
import { PageHeader } from '../components/Common';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Upload, FileSpreadsheet, Download, AlertTriangle, CheckCircle2,
  UserPlus, UserCheck, Loader2,
} from 'lucide-react';
import { toast } from 'sonner';

// Los dos campos de la ficha donde caen las alergias del Excel (2026-09-21).
// Antes la columna «Alergias» iba a parar a un campo que no leía nadie, así
// que tampoco había nada que enseñar aquí; ahora entra en la ficha de verdad y
// quien aplica la importación tiene que verlo ANTES de aplicarla.
const ALERGIAS = new Set(['alergias', 'alergias_text']);

/** La casilla y el texto, en las palabras con que se leen en la ficha. */
function textoDeAlergias(valor) {
  if (valor === true) return 'sí';
  if (valor === false) return 'no';
  return String(valor ?? '');
}

export default function ImportPacientesPage() {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);

  const downloadTemplate = async () => {
    try {
      const r = await api.get('/api/patients-import/template', { responseType: 'blob' });
      const blob = new Blob([r.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'plantilla_pacientes.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('No se pudo descargar la plantilla');
    }
  };

  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    setFile(f || null);
    setPreview(null);
  };

  const uploadPreview = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post('/api/patients-import/preview', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPreview(r.data);
      toast.success(
        `Análisis: ${r.data.matched.length} a actualizar · ${r.data.new.length} nuevos · ${r.data.errors.length} errores`,
      );
    } catch (e) {
      const msg = e?.response?.data?.detail || 'No se pudo procesar el Excel';
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const commitImport = async () => {
    if (!preview) return;
    setBusy(true);
    try {
      const r = await api.post('/api/patients-import/commit', {
        matched: preview.matched,
        new: preview.new,
      });
      toast.success(`Importación aplicada: ${r.data.created} nuevos · ${r.data.updated} actualizados`);
      setPreview(null);
      setFile(null);
      // Reset file input
      const inp = document.getElementById('import-file-input');
      if (inp) inp.value = '';
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Fallo al aplicar la importación';
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="import-pacientes-page">
      <PageHeader
        title="Importar pacientes antiguos"
        description="Sube un Excel con la base de datos de pacientes existentes. El sistema actualiza los datos vacíos de los pacientes que ya tienes y crea los que faltan."
      />

      {/* PASO 1 — Plantilla y subida */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-[#13231E]">
            <FileSpreadsheet className="w-5 h-5" /> 1. Sube tu fichero Excel
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              variant="outline"
              onClick={downloadTemplate}
              data-testid="download-template-btn"
            >
              <Download className="w-4 h-4 mr-2" /> Descargar plantilla
            </Button>
            <span className="text-xs text-muted-foreground">
              Recomendado: úsala como referencia para asegurarte que tienes las columnas correctas.
            </span>
          </div>

          <div className="flex items-center gap-2 flex-wrap pt-2 border-t">
            <input
              id="import-file-input"
              type="file"
              accept=".xlsx,.xls,.xlsm"
              onChange={onFileChange}
              className="block text-sm text-muted-foreground file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-[#2E6E5E]/10 file:text-[#13231E] hover:file:bg-[#2E6E5E]/20"
              data-testid="import-file-input"
            />
            <Button
              onClick={uploadPreview}
              disabled={!file || busy}
              className="bg-[#2E6E5E] hover:bg-[#1d324e] text-white"
              data-testid="import-preview-btn"
            >
              {busy ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analizando…</>
              ) : (
                <><Upload className="w-4 h-4 mr-2" /> Analizar fichero</>
              )}
            </Button>
          </div>

          <div className="text-xs text-muted-foreground bg-[rgba(46,110,94,0.06)] rounded p-3">
            <b>Identificación de duplicados:</b> el sistema busca pacientes que ya
            existan por <b>DNI</b> o por <b>Teléfono</b>. Si encuentra coincidencia,
            actualiza <b>solo los campos vacíos</b>, sin sobrescribir nada.
            Las filas sin DNI ni teléfono no se importan y aparecen en la sección
            de errores para que las revises manualmente.
          </div>
        </CardContent>
      </Card>

      {/* PASO 2 — Preview */}
      {preview && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-[#13231E]">
              <CheckCircle2 className="w-5 h-5" /> 2. Revisa antes de aplicar
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
              <SummaryBox
                icon={<UserCheck className="w-5 h-5" />}
                color="#1D3F6E"
                label="Pacientes a actualizar"
                value={preview.matched.length}
                desc="ya existen — se rellenan campos vacíos"
              />
              <SummaryBox
                icon={<UserPlus className="w-5 h-5" />}
                color="#4AABE0"
                label="Pacientes nuevos"
                value={preview.new.length}
                desc="se crean en la base de datos"
              />
              <SummaryBox
                icon={<AlertTriangle className="w-5 h-5" />}
                color="#c97800"
                label="Errores"
                value={preview.errors.length}
                desc="filas que no se pueden importar"
              />
            </div>

            <Tabs defaultValue="updates">
              <TabsList className="grid grid-cols-3 mb-3">
                <TabsTrigger value="updates" data-testid="preview-tab-updates">
                  Actualizar ({preview.matched.length})
                </TabsTrigger>
                <TabsTrigger value="new" data-testid="preview-tab-new">
                  Nuevos ({preview.new.length})
                </TabsTrigger>
                <TabsTrigger value="errors" data-testid="preview-tab-errors">
                  Errores ({preview.errors.length})
                </TabsTrigger>
              </TabsList>

              <TabsContent value="updates">
                <UpdatesTable rows={preview.matched} />
              </TabsContent>
              <TabsContent value="new">
                <NewTable rows={preview.new} />
              </TabsContent>
              <TabsContent value="errors">
                <ErrorsTable rows={preview.errors} />
              </TabsContent>
            </Tabs>

            <div className="flex items-center justify-end gap-2 pt-4 border-t mt-4">
              <Button
                variant="outline"
                onClick={() => { setPreview(null); setFile(null); }}
                data-testid="import-cancel-btn"
              >
                Cancelar
              </Button>
              <Button
                onClick={commitImport}
                disabled={busy || (preview.matched.length === 0 && preview.new.length === 0)}
                className="bg-[#2E6E5E] hover:bg-[#1d324e] text-white"
                data-testid="import-commit-btn"
              >
                {busy ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Importando…</>
                ) : (
                  <>Aplicar importación ({preview.matched.length + preview.new.length})</>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function SummaryBox({ icon, color, label, value, desc }) {
  return (
    <div
      className="rounded-lg border p-3 flex items-center gap-3"
      style={{ borderColor: `${color}33`, background: `${color}08` }}
    >
      <div
        className="w-10 h-10 rounded-md flex items-center justify-center"
        style={{ background: `${color}1f`, color }}
      >
        {icon}
      </div>
      <div className="flex-1">
        <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
        <div className="text-2xl font-semibold" style={{ color }}>{value}</div>
        <div className="text-[11px] text-muted-foreground">{desc}</div>
      </div>
    </div>
  );
}

function UpdatesTable({ rows }) {
  if (!rows.length) {
    return <div className="text-sm text-muted-foreground text-center py-6">Sin pacientes a actualizar.</div>;
  }
  return (
    <div className="border rounded-md overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Fila</TableHead>
            <TableHead>Paciente existente</TableHead>
            <TableHead>Datos en el Excel</TableHead>
            <TableHead>Campos a rellenar</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map(r => (
            <TableRow key={r.row}>
              <TableCell className="text-xs text-muted-foreground">#{r.row}</TableCell>
              <TableCell className="text-sm font-medium">{r.existing_name}</TableCell>
              <TableCell className="text-xs text-muted-foreground">{r.incoming_name}</TableCell>
              <TableCell className="text-xs">
                {r.updates_count === 0 ? (
                  <span className="text-muted-foreground">— ya completo —</span>
                ) : (
                  Object.keys(r.updates).map(k => (
                    <span key={k} className={`inline-block px-1.5 py-0.5 rounded mr-1 mb-1 text-[10px] ${
                      ALERGIAS.has(k) ? 'bg-red-50 text-red-900' : 'bg-blue-50 text-blue-900'}`}
                    >
                      {/* Las alergias se enseñan ENTERAS antes de aplicar: van
                          a una ficha que ya existe y encienden el aviso rojo
                          del paciente (2026-09-21). Ver el campo con su
                          contenido es lo único que permite pararlo a tiempo. */}
                      {ALERGIAS.has(k) ? `${k}: ${textoDeAlergias(r.updates[k])}` : k}
                    </span>
                  ))
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function NewTable({ rows }) {
  if (!rows.length) {
    return <div className="text-sm text-muted-foreground text-center py-6">Sin pacientes nuevos.</div>;
  }
  return (
    <div className="border rounded-md overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Fila</TableHead>
            <TableHead>Nombre</TableHead>
            <TableHead>DNI</TableHead>
            <TableHead>Teléfono</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>F. nac.</TableHead>
            {/* La columna «Alergias» del Excel entra en la ficha del paciente
                (2026-09-21): se ve aquí antes de aplicar nada. */}
            <TableHead>Alergias</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map(r => (
            <TableRow key={r.row}>
              <TableCell className="text-xs text-muted-foreground">#{r.row}</TableCell>
              <TableCell className="text-sm">
                {[r.parsed.name, r.parsed.surname1, r.parsed.surname2].filter(Boolean).join(' ')}
              </TableCell>
              <TableCell className="text-xs font-mono">{r.parsed.nif || '—'}</TableCell>
              <TableCell className="text-xs font-mono">{r.parsed.phone || '—'}</TableCell>
              <TableCell className="text-xs">{r.parsed.email || '—'}</TableCell>
              <TableCell className="text-xs">{r.parsed.birth_date || '—'}</TableCell>
              <TableCell
                className={`text-xs ${r.parsed.alergias === true ? 'text-red-700 font-medium' : ''}`}
                data-testid={`nuevo-alergias-${r.row}`}
              >
                {r.parsed.alergias_text || '—'}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function ErrorsTable({ rows }) {
  if (!rows.length) {
    return <div className="text-sm text-muted-foreground text-center py-6">Sin errores. ✓</div>;
  }
  return (
    <div className="border rounded-md">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Fila</TableHead>
            <TableHead>Motivo</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map(r => (
            <TableRow key={r.row}>
              <TableCell className="text-xs text-muted-foreground">#{r.row}</TableCell>
              <TableCell className="text-sm text-amber-900 dark:text-amber-400">{r.reason}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
